/* cSpell:ignore ecommerce */

import TagManagerLib from 'react-gtm-module';

import { getEnvVars } from '@order/utils';

import { logInitialize, logTrackEventPayload } from './google-analytics.logger';
import {
  type GoogleAnalyticsEventPayload,
  type GoogleAnalyticsHelpers,
} from './google-analytics.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Helpers for tracking Google Analytics events.
 *
 * NOTE: On the web platform, we use Google Tag Manager.
 * @see {@link https://marketingplatform.google.com/about/tag-manager/}
 */
export const googleAnalytics: GoogleAnalyticsHelpers = {
  /**
   * @see {@link https://github.com/alinemorelli/react-gtm#usage}
   */
  async initialize() {
    const { GTM_AUTH, GTM_CONTAINER_ID, GTM_ENVIRONMENT } = getEnvVars();

    // Disable GTM in development environment or when GTM_CONTAINER_ID is not set
    if (!GTM_CONTAINER_ID || __DEV__) {
      console.log('Google Tag Manager disabled in development environment');
      return;
    }

    logInitialize();

    TagManagerLib.initialize({
      auth: GTM_AUTH,
      gtmId: GTM_CONTAINER_ID,
      preview: GTM_ENVIRONMENT,
      dataLayerName: DATA_LAYER_NAME,
    });
  },

  /**
   * @see {@link https://github.com/alinemorelli/react-gtm#datalayer}
   */
  async track(payload) {
    const { GTM_CONTAINER_ID } = getEnvVars();

    // Skip tracking in development environment or when GTM_CONTAINER_ID is not set
    if (!GTM_CONTAINER_ID || __DEV__) {
      return;
    }

    logTrackEventPayload(payload);

    TagManagerLib.dataLayer({
      dataLayer: handleEcommerceData(payload),
      dataLayerName: DATA_LAYER_NAME,
    });
  },

  resetAnalyticsData() {
    const { GTM_CONTAINER_ID } = getEnvVars();

    // Skip reset in development environment or when GTM_CONTAINER_ID is not set
    if (!GTM_CONTAINER_ID || __DEV__) {
      return;
    }

    // eslint-disable-next-line functional/immutable-data
    // @ts-expect-error Index expression is not of type 'number'
    window[DATA_LAYER_NAME] = [];
  },
};

/**
 * This handles a requirement to clear the ecommerce object and push a new one.
 * It happens on every event that contains items, only needed for the data layer.
 * The payload needs to be duplicated outside and inside the ecommerce field.
 * @see {@link: https://sweetgreen.atlassian.net/browse/CELS-1247?focusedCommentId=109476}
 */
function handleEcommerceData(payload: GoogleAnalyticsEventPayload) {
  if (!payload.items) return payload;

  clearPreviousEcommerceObject();

  return {
    ...payload,
    ecommerce: payload,
  };
}

function clearPreviousEcommerceObject() {
  const { GTM_CONTAINER_ID } = getEnvVars();

  // Skip clearing in development environment or when GTM_CONTAINER_ID is not set
  if (!GTM_CONTAINER_ID || __DEV__) {
    return;
  }

  TagManagerLib.dataLayer({
    dataLayer: { ecommerce: null },
    dataLayerName: DATA_LAYER_NAME,
  });
}

// ─── Constants ───────────────────────────────────────────────────────────────

const DATA_LAYER_NAME = 'dataLayer';
